// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { QueryKey } from "@tanstack/react-query";
import { sql } from "kysely";

import { executeSqlV2, queryBuilder } from "~/api/materialize";

export const buildAllowedClusterReplicaSizesQuery = () => {
  return sql<{
    allowed_cluster_replica_sizes: string;
  }>`SHOW allowed_cluster_replica_sizes`;
};

const LEGACY_SIZE_PATTERN = "^[0-9]+([a-zA-Z]*cc[xX]?|C)$";

/**
 * A size's family (legacy cc, t-shirt, M.1, D.1, ...), the key
 * `buildAvailableClusterReplicaSizesQuery` groups sizes by. The two must agree.
 */
export function clusterSizeFamily(size: string) {
  if (new RegExp(LEGACY_SIZE_PATTERN).test(size)) return "0_legacy";
  if (size.includes("-")) return `1_${size.split("-")[0]}`;
  return "2_tshirt";
}

export const buildAvailableClusterReplicaSizesQuery = () => {
  return (
    queryBuilder
      .selectFrom("mz_cluster_replica_sizes")
      .select("size")
      // We don't want to show the mz_probe size in the UI
      .where("size", "not like", "mz_%")
      // Group sizes by family so related sizes stay contiguous, then sort by
      // cost within each family.
      .orderBy(
        sql<string>`CASE
          WHEN size ~ ${sql.lit(LEGACY_SIZE_PATTERN)} THEN '0_legacy'
          WHEN size LIKE '%-%' THEN '1_' || split_part(size, '-', 1)
          ELSE '2_tshirt'
        END`,
      )
      .orderBy("credits_per_hour")
  );
};
/**
 * Fetches available cluster sizes based on LaunchDarkly configuration
 */
export default async function fetchAvailableClusterSizes({
  queryKey,
  requestOptions,
}: {
  queryKey: QueryKey;
  requestOptions: RequestInit;
}) {
  const compiledQueries = [
    buildAllowedClusterReplicaSizesQuery().compile(queryBuilder),
    buildAvailableClusterReplicaSizesQuery().compile(),
  ] as const;

  let sizes: string[] | null = null;

  const [allowedSizesRes, availableSizesRes] = await executeSqlV2({
    queries: compiledQueries,
    queryKey,
    requestOptions,
  });

  if (allowedSizesRes.rows && availableSizesRes.rows) {
    // There exists cases where the allowed sizes is an empty string (i.e. self-hosted, private stacks).
    // If the allowed sizes is an empty string, we want to return all available sizes.
    const allowedSizesStr =
      allowedSizesRes.rows[0]?.allowed_cluster_replica_sizes.trim() ?? "";

    const allowedSizes =
      allowedSizesStr !== ""
        ? allowedSizesStr
            .split(",")
            .map((v: string) => v.replaceAll('"', "").trim())
        : [];

    const availableSizes = availableSizesRes.rows.map((row) => row.size);

    sizes = availableSizes.filter(
      (size) => allowedSizes.length === 0 || allowedSizes.includes(size),
    );
  }

  return sizes;
}
